import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import RevenueChart from './RevenueChart';
import {ChartStyle, ChartTabOption, ChartTabs} from '../work_and_expenses/Chart.styled';
import {createFragmentContainer, graphql} from 'react-relay';
import {decompressAggregatedData} from '../../util/DataDecompressUtil';
import CostChart from './CostChart';
import ProfitChart from './ProfitChart';
import Util from '../../../../../../forecast-app/shared/util/util';
import {getCurrency} from '../../util/BudgetUtils';
import {getTimeUnit} from '../../util/ChartsUtil';
import {BUDGET_MONEY_VIEWS, BUDGET_TYPE, PERIOD_BUDGET_TYPE} from '../../../../../../constants';
import {hasFeatureFlag} from '../../../../../../forecast-app/shared/util/FeatureUtil';
import moment from 'moment';

const RevenueCostProfitCharts = ({viewer, eyeOptionMap, hasBaseline, selectedViewType}) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const {project} = viewer;
	const currencySymbol = Util.GetCurrencySymbol(getCurrency(viewer.company, viewer.project));
	const projectFirstDate =
		Util.CreateNonUtcMomentDate(project.projectFirstDateYear, project.projectFirstDateMonth, project.projectFirstDateDay) ||
		moment();
	const projectLastDate =
		Util.CreateNonUtcMomentDate(project.projectLastDateYear, project.projectLastDateMonth, project.projectLastDateDay) ||
		moment();
	const isRetainerFixedPriceV2 =
		hasFeatureFlag('retainer_fixed_price_v2') &&
		project.budgetType === BUDGET_TYPE.RETAINER &&
		project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE;
	const showFixedPriceGraphs =
		[BUDGET_TYPE.FIXED_PRICE, BUDGET_TYPE.FIXED_PRICE_V2].includes(project.budgetType) || isRetainerFixedPriceV2;
	const isUsingNewFixedPriceCalculation = isRetainerFixedPriceV2 || project.budgetType === BUDGET_TYPE.FIXED_PRICE_V2;
	const isExpenseView = BUDGET_MONEY_VIEWS.EXPENSES === selectedViewType;
	const isBillableProject = project.budgetType !== BUDGET_TYPE.NON_BILLABLE;
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();

	const aggregatedFinancialNumbers = decompressAggregatedData(project.aggregatedFinancialNumbers, [
		'actualRevenue',
		'plannedRevenue',
		'forecastRevenue',
		'baselineRevenue',
		'actualCost',
		'plannedCost',
		'forecastCost',
		'baselineCost',
		'actualProfit',
		'plannedProfit',
		'forecastProfit',
		'baselineProfit',
		'rateBasedForecastRevenue',
	]);

	const data = {
		actualRevenueChartData: [],
		plannedRevenueChartData: [],
		forecastRevenueChartData: [],
		baselineRevenueChartData: [],

		actualCostChartData: [],
		plannedCostChartData: [],
		forecastCostChartData: [],
		baselineCostChartData: [],

		actualProfitChartData: [],
		plannedProfitChartData: [],
		forecastProfitChartData: [],
		baselineProfitChartData: [],

		fixedPriceChartData: [],
		rateBasedForecastRevenueChartData: [],
	};
	aggregatedFinancialNumbers.forEach(financialNumbers => {
		const isTodayDate = financialNumbers.date.isSame(moment(), 'days');
		data.actualRevenueChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.actualRevenueAccumulated,
		});
		data.actualCostChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.actualCostAccumulated,
		});
		data.actualProfitChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.actualProfitAccumulated,
		});
		data.plannedRevenueChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.plannedRevenueAccumulated,
		});
		data.plannedCostChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.plannedCostAccumulated,
		});
		data.plannedProfitChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.plannedProfitAccumulated,
		});
		data.baselineRevenueChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.baselineRevenueAccumulated,
		});
		data.baselineCostChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.baselineCostAccumulated,
		});
		data.baselineProfitChartData.push({
			date: financialNumbers.date.clone(),
			value: financialNumbers.baselineProfitAccumulated,
		});
		if (!isUsingNewFixedPriceCalculation && !financialNumbers.date.isBefore(moment(), 'days')) {
			data.forecastRevenueChartData.push({
				date: financialNumbers.date.clone(),
				value: isTodayDate ? financialNumbers.actualRevenueAccumulated : financialNumbers.forecastRevenueAccumulated,
			});
			data.forecastCostChartData.push({
				date: financialNumbers.date.clone(),
				value: isTodayDate ? financialNumbers.actualCostAccumulated : financialNumbers.forecastCostAccumulated,
			});
			data.forecastProfitChartData.push({
				date: financialNumbers.date.clone(),
				value: isTodayDate ? financialNumbers.actualProfitAccumulated : financialNumbers.forecastProfitAccumulated,
			});
		}
		if (isUsingNewFixedPriceCalculation && !isExpenseView) {
			data.rateBasedForecastRevenueChartData.push({
				date: financialNumbers.date.clone(),
				value: financialNumbers.rateBasedForecastRevenueAccumulated,
			});
		}
	});

	data.fixedPriceChartData.push({date: projectFirstDate, value: project.budget});
	data.fixedPriceChartData.push({date: projectLastDate, value: project.budget});

	const modifyChartOptions = options => {
		options.scales.xAxes[0].time.unit = getTimeUnit(projectFirstDate, projectLastDate);
		options.scales.yAxes[0].ticks.callback = label => Util.getFormattedNumberWithCurrency(currencySymbol, label, intl);
		options.tooltips.callbacks.label = (tooltipItem, data) => {
			const item = data.datasets[tooltipItem.datasetIndex];
			const dataItem = data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
			if (dataItem.customData && dataItem.customData.hideLabel) {
				return '';
			} else {
				const dataSetLabel = item.label ? `${item.label}: ` : '';
				return `${dataSetLabel}${Util.getFormattedNumberWithCurrency(currencySymbol, tooltipItem.value, intl)}`;
			}
		};
		return options;
	};

	const charts = [
		isBillableProject
			? {
					name: formatMessage({id: `project_budget.revenue`}),
					render: () => (
						<RevenueChart
							data={data}
							project={project}
							hasBaseline={hasBaseline}
							eyeOptionMap={eyeOptionMap}
							modifyChartOptions={modifyChartOptions}
							showFixedPriceGraphs={showFixedPriceGraphs}
							isUsingNewFixedPriceCalculation={isUsingNewFixedPriceCalculation}
						/>
					),
			  }
			: null,
		!hasLimitedFinancialAccess
			? {
					name: formatMessage({id: `common.cost`}),
					render: () => (
						<CostChart
							data={data}
							project={project}
							hasBaseline={hasBaseline}
							eyeOptionMap={eyeOptionMap}
							modifyChartOptions={modifyChartOptions}
						/>
					),
			  }
			: null,
		!hasLimitedFinancialAccess && isBillableProject
			? {
					name: formatMessage({id: `common.profit`}),
					render: () => (
						<ProfitChart
							data={data}
							project={project}
							hasBaseline={hasBaseline}
							eyeOptionMap={eyeOptionMap}
							modifyChartOptions={modifyChartOptions}
						/>
					),
			  }
			: null,
	].filter(Boolean);

	return (
		<>
			<ChartTabs>
				{charts.length > 1 &&
					charts.map((item, index) => {
						return (
							<ChartTabOption
								key={'money_chart_' + index}
								active={index === activeTabIndex}
								onClick={() => setActiveTabIndex(index)}
							>
								{item.name}
							</ChartTabOption>
						);
					})}
			</ChartTabs>
			{charts.length > 0 && <ChartStyle>{charts[activeTabIndex].render()}</ChartStyle>}
		</>
	);
};

const revenueCostProfitChartsQuery = graphql`
	query RevenueCostProfitCharts_Query(
		$projectId: String
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
		$searchQuery: TaskSearchQueryType
	) {
		viewer {
			id
			actualPersonId
			userType
			component(name: "project_budget_v3_revenue_cost_profit_charts")
			...RevenueCostProfitCharts_viewer
				@arguments(
					projectId: $projectId
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					searchQuery: $searchQuery
				)
		}
	}
`;

export {revenueCostProfitChartsQuery};

export default createFragmentContainer(RevenueCostProfitCharts, {
	viewer: graphql`
		fragment RevenueCostProfitCharts_viewer on Viewer
		@argumentDefinitions(
			projectId: {type: "String"}
			startYear: {type: "Int!"}
			startMonth: {type: "Int!"}
			startDay: {type: "Int!"}
			endYear: {type: "Int!"}
			endMonth: {type: "Int!"}
			endDay: {type: "Int!"}
			searchQuery: {type: "TaskSearchQueryType"}
		) {
			company {
				currency
			}
			project(id: $projectId) {
				projectFirstDateYear
				projectFirstDateMonth
				projectFirstDateDay
				projectLastDateYear
				projectLastDateMonth
				projectLastDateDay
				rateCard {
					currency
				}
				estimationUnit
				budget
				budgetType
				billable
				defaultPeriodBudgetType
				aggregatedFinancialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					aggregates: [
						actualRevenue
						plannedRevenue
						forecastRevenue
						baselineRevenue
						actualCost
						plannedCost
						forecastCost
						baselineCost
						actualProfit
						plannedProfit
						forecastProfit
						baselineProfit
						rateBasedForecastRevenue
					]
					aggregateLevel: DAY
					addAccumulatedNumbers: true
					searchQuery: $searchQuery
					convertToProjectCurrency: true
				)
			}
		}
	`,
});
